import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PnlDashboard = () => {
  const [records, setRecords] = useState([]);
  const [aggregates, setAggregates] = useState([]);
  const [groupBy, setGroupBy] = useState('campaign');
  const [period, setPeriod] = useState('');
  const [filterCampaign, setFilterCampaign] = useState('');
  const [filterOperator, setFilterOperator] = useState('');
  const [filterBU, setFilterBU] = useState('');
  const [sortCol, setSortCol] = useState('spend');
  const [sortDir, setSortDir] = useState('desc');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const params = new URLSearchParams();
        if (period) params.set('period', period);
        if (filterCampaign) params.set('campaign_id', filterCampaign);
        if (filterOperator) params.set('operator_id', filterOperator);
        if (filterBU) params.set('business_unit_id', filterBU);
        params.set('limit', '500');

        const [recRes, aggRes] = await Promise.all([
          axios.get(`${API}/performance/pnl?${params.toString()}`),
          axios.get(`${API}/performance/pnl/aggregates?group_by=${groupBy}`),
        ]);
        setRecords(recRes.data.records || []);
        setAggregates(aggRes.data.aggregates || []);
      } catch (e) {
        console.error('Failed to load P&L:', e);
      } finally { setLoading(false); }
    };
    fetch();
  }, [period, filterCampaign, filterOperator, filterBU, groupBy]);

  // Summary cards
  const summary = useMemo(() => {
    const totalSpend = records.reduce((s, r) => s + r.spend, 0);
    const totalRevenue = records.reduce((s, r) => s + r.revenue, 0);
    const totalProfit = records.reduce((s, r) => s + r.profit, 0);
    const roi = totalSpend > 0 ? ((totalProfit / totalSpend) * 100) : 0;
    const discrepancies = records.filter(r => r.discrepancy_flag).length;
    return { totalSpend, totalRevenue, totalProfit, roi, discrepancies };
  }, [records]);

  // Sort records
  const sortedRecords = useMemo(() => {
    return [...records].sort((a, b) => {
      const valA = a[sortCol] || 0;
      const valB = b[sortCol] || 0;
      return sortDir === 'desc' ? valB - valA : valA - valB;
    });
  }, [records, sortCol, sortDir]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const SortHeader = ({ col, label }) => (
    <th
      className="text-right text-slate-400 py-3 px-3 cursor-pointer hover:text-white transition-colors select-none"
      onClick={() => handleSort(col)}
    >
      {label} {sortCol === col && (sortDir === 'desc' ? '↓' : '↑')}
    </th>
  );

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500" />
    </div>
  );

  return (
    <div className="space-y-6" data-testid="pnl-dashboard">
      <div>
        <h2 className="text-2xl font-bold text-white">P&L Dashboard</h2>
        <p className="text-slate-400 mt-1">Profit & Loss across campaigns</p>
      </div>

      {/* Filter Bar */}
      <div className="flex items-center gap-4 flex-wrap bg-slate-900/50 border border-slate-800 rounded-xl p-4" data-testid="pnl-filters">
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">Period:</span>
          <select value={period} onChange={e => setPeriod(e.target.value)} data-testid="period-filter"
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:border-emerald-500 focus:outline-none">
            <option value="">All Time</option>
            <option value="day">Last 24h</option>
            <option value="week">Last Week</option>
            <option value="month">Last Month</option>
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-sm">Aggregate:</span>
          <select value={groupBy} onChange={e => setGroupBy(e.target.value)} data-testid="group-by-filter"
            className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-sm focus:border-emerald-500 focus:outline-none">
            <option value="campaign">By Campaign</option>
            <option value="operator">By Operator</option>
            <option value="offer">By Offer</option>
            <option value="business_unit">By Business Unit</option>
          </select>
        </div>
        <a
          href={`${API}/export/pnl${period ? '?period=' + period : ''}`}
          className="ml-auto px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-slate-300 hover:text-white hover:border-slate-600 transition-colors self-center"
          data-testid="export-pnl-csv"
        >
          Export CSV
        </a>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4" data-testid="pnl-summary-cards">
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 text-center">
          <div className="text-sm text-slate-400 mb-1">Total Spend</div>
          <div className="text-2xl font-bold text-red-400" data-testid="total-spend">${summary.totalSpend.toFixed(0)}</div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 text-center">
          <div className="text-sm text-slate-400 mb-1">Total Revenue</div>
          <div className="text-2xl font-bold text-emerald-400" data-testid="total-revenue">${summary.totalRevenue.toFixed(0)}</div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 text-center">
          <div className="text-sm text-slate-400 mb-1">Total Profit</div>
          <div className={`text-2xl font-bold ${summary.totalProfit >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="total-profit">
            ${summary.totalProfit.toFixed(0)}
          </div>
        </div>
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5 text-center">
          <div className="text-sm text-slate-400 mb-1">Overall ROI</div>
          <div className={`text-2xl font-bold ${summary.roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="overall-roi">
            {summary.roi.toFixed(1)}%
          </div>
        </div>
      </div>

      {/* Aggregates Table */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-800">
          <h3 className="text-white font-semibold">
            Aggregates — by {groupBy.replace('_', ' ')}
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pnl-aggregates-table">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left text-slate-400 py-3 px-4">Name</th>
                <th className="text-right text-slate-400 py-3 px-3">Spend</th>
                <th className="text-right text-slate-400 py-3 px-3">Revenue</th>
                <th className="text-right text-slate-400 py-3 px-3">Profit</th>
                <th className="text-right text-slate-400 py-3 px-3">ROAS</th>
                <th className="text-right text-slate-400 py-3 px-3">Discrepancies</th>
              </tr>
            </thead>
            <tbody>
              {aggregates.map((agg, i) => (
                <tr key={i} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                  <td className="py-3 px-4 text-white font-medium">{agg.name || agg.group_id}</td>
                  <td className="py-3 px-3 text-right text-red-300">${agg.total_spend?.toFixed(0)}</td>
                  <td className="py-3 px-3 text-right text-emerald-300">${agg.total_revenue?.toFixed(0)}</td>
                  <td className={`py-3 px-3 text-right font-medium ${agg.total_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    ${agg.total_profit?.toFixed(0)}
                  </td>
                  <td className="py-3 px-3 text-right text-slate-300">{agg.avg_roas?.toFixed(2)}x</td>
                  <td className="py-3 px-3 text-right">
                    {agg.discrepancy_count > 0 ? (
                      <span className="text-amber-400">{agg.discrepancy_count}</span>
                    ) : (
                      <span className="text-slate-500">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Per-Record Table (sortable) */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-800 flex items-center justify-between">
          <h3 className="text-white font-semibold">Daily Records ({sortedRecords.length})</h3>
          {summary.discrepancies > 0 && (
            <span className="text-amber-400 text-sm">{summary.discrepancies} reconciliation flags</span>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pnl-records-table">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="text-left text-slate-400 py-3 px-4">Campaign</th>
                <th className="text-left text-slate-400 py-3 px-3">Date</th>
                <SortHeader col="spend" label="Spend" />
                <SortHeader col="revenue" label="Revenue" />
                <SortHeader col="profit" label="Profit" />
                <SortHeader col="roas" label="ROAS" />
                <th className="text-right text-slate-400 py-3 px-3">Reconciliation</th>
              </tr>
            </thead>
            <tbody>
              {sortedRecords.slice(0, 50).map((r, i) => (
                <tr key={i} className={`border-b border-slate-800/50 ${r.discrepancy_flag ? 'bg-amber-500/5' : ''}`}>
                  <td className="py-3 px-4 text-white text-xs max-w-[200px] truncate">{r.campaign_name}</td>
                  <td className="py-3 px-3 text-slate-400">{new Date(r.date).toLocaleDateString()}</td>
                  <td className="py-3 px-3 text-right text-red-300">${r.spend?.toFixed(0)}</td>
                  <td className="py-3 px-3 text-right text-emerald-300">${r.revenue?.toFixed(0)}</td>
                  <td className={`py-3 px-3 text-right font-medium ${r.profit >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    ${r.profit?.toFixed(0)}
                  </td>
                  <td className="py-3 px-3 text-right text-slate-300">{r.roas?.toFixed(2)}x</td>
                  <td className="py-3 px-3 text-right">
                    {r.discrepancy_flag ? (
                      <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded" data-testid="discrepancy-flag">
                        {r.discrepancy_pct?.toFixed(1)}% off
                      </span>
                    ) : (
                      <span className="text-slate-600 text-xs">OK</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PnlDashboard;
